import { useEffect, useRef } from 'react';

/**
 * 監聽 window scroll，靠近底部時觸發 callback。
 * 內建 throttle，listener 只綁一次，callback 透過 ref 取最新版避免 stale closure。
 * 注意：頁面外層容器不可設 overflow:auto + 固定 height，否則 window 不會 scroll。
 *
 * @param {Function} onReachBottom 觸發時呼叫；callback 內請自行檢查 isLoading / 還有下一頁等條件
 * @param {Object}   [options]
 * @param {number}   [options.distance=1000] 距底多少 px 時觸發
 * @param {number}   [options.delay=500]     throttle 間隔
 */
export function useInfiniteScroll(onReachBottom, { distance = 1000, delay = 500 } = {}) {
  const cbRef = useRef(onReachBottom);
  cbRef.current = onReachBottom;

  useEffect(() => {
    let timer = null;

    const handler = () => {
      if (timer) return;
      timer = setTimeout(() => {
        timer = null;
        const scrollY = window.scrollY;
        const innerHeight = window.innerHeight;
        const scrollHeight = document.documentElement.scrollHeight;
        const remaining = scrollHeight - (scrollY + innerHeight);
        if (remaining < distance) {
          cbRef.current?.();
        }
      }, delay);
    };

    window.addEventListener('scroll', handler, { passive: true });
    return () => {
      window.removeEventListener('scroll', handler);
      if (timer) clearTimeout(timer);
    };
  }, [distance, delay]);
}
